import { useQuery } from '@tanstack/react-query';
import { apiHelpers } from '@/lib/api';
import { CreditCard, AlertTriangle, FileText, Wallet } from 'lucide-react';
import { formatMoney, formatCredits } from '@/lib/money';

/**
 * SUPER_ADMIN-only billing panel for a project's team. One place with everything
 * needed to support a customer about payments/subscriptions — visible only to
 * super-admins (from their account or via ghost login). Reads the consolidated
 * /admin/billing/teams/:teamId/summary endpoint.
 *
 * Invoice amounts are canonical MINOR units (paise/cents) — the old "guess the
 * unit" heuristic is gone; the `/admin/billing/invoices/unit-audit` + backfill
 * endpoints normalize any legacy rows so display can trust minor units.
 */

function statusBadge(status?: string) {
  const s = (status || '').toLowerCase();
  const cls =
    s === 'active'
      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
      : s === 'past_due' || s === 'failed'
        ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
        : 'bg-muted text-muted-foreground';
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{status || '—'}</span>
  );
}

export function SuperAdminBillingPanel({ teamId }: { teamId: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-billing-summary', teamId],
    queryFn: () => apiHelpers.getTeamBillingSummary(teamId),
    enabled: Boolean(teamId),
  });

  const d = (data as any)?.data?.data;

  return (
    <div className="bg-card rounded-xl border">
      <div className="flex items-center gap-2 border-b px-5 py-3">
        <CreditCard className="text-primary h-4 w-4" />
        <h3 className="text-sm font-semibold">Billing & subscriptions</h3>
        <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
          super-admin
        </span>
      </div>

      {isLoading ? (
        <div className="text-muted-foreground p-5 text-sm">Loading billing…</div>
      ) : isError || !d ? (
        <div className="text-muted-foreground p-5 text-sm">Couldn't load billing summary.</div>
      ) : (
        <div className="space-y-5 p-5">
          {/* Subscription */}
          <div>
            <div className="text-muted-foreground mb-1 text-xs font-medium uppercase">
              Platform subscription
            </div>
            {d.subscription ? (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                <span className="font-semibold capitalize">{d.subscription.plan}</span>
                {statusBadge(d.subscription.status)}
                {d.subscription.gateway && (
                  <span className="text-muted-foreground">via {d.subscription.gateway}</span>
                )}
                {d.subscription.currentPeriodEnd && (
                  <span className="text-muted-foreground">
                    next: {new Date(d.subscription.currentPeriodEnd).toLocaleDateString()}
                  </span>
                )}
                {d.subscription.gatewaySubscriptionId && (
                  <code className="bg-muted rounded px-1.5 py-0.5 text-xs">
                    {d.subscription.gatewaySubscriptionId}
                  </code>
                )}
                {d.subscription.source && d.subscription.source !== 'subscription' && (
                  <span className="text-muted-foreground text-xs italic">
                    (no subscription doc — plan from team record)
                  </span>
                )}
              </div>
            ) : (
              <div className="text-muted-foreground text-sm">No subscription record.</div>
            )}
          </div>

          {/* Gateway subscriptions (mapping) */}
          {Array.isArray(d.gatewaySubscriptions) && d.gatewaySubscriptions.length > 0 && (
            <div>
              <div className="text-muted-foreground mb-1 text-xs font-medium uppercase">
                Gateway subscriptions
              </div>
              <div className="space-y-1">
                {d.gatewaySubscriptions.map((g: any, i: number) => (
                  <div key={i} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                    <span className="font-medium">{g.product?.id || g.product?.type}</span>
                    <code className="bg-muted rounded px-1.5 py-0.5 text-xs">
                      {g.gatewaySubscriptionId}
                    </code>
                    {statusBadge(g.status)}
                    {(g.status === 'failed' || g.status === 'past_due') && (
                      <span className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                        <AlertTriangle className="h-3 w-3" />
                        {g.lastFailureReason || 'payment issue'}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Credits */}
          {d.credits && (
            <div className="flex items-center gap-2 text-sm">
              <Wallet className="text-muted-foreground h-4 w-4" />
              <span className="text-muted-foreground">Credits:</span>
              <span className="font-medium">
                {formatCredits(d.credits.balance, d.credits.currency)}
              </span>
            </div>
          )}

          {/* Invoices / payments */}
          <div>
            <div className="text-muted-foreground mb-1 flex items-center gap-1.5 text-xs font-medium uppercase">
              <FileText className="h-3.5 w-3.5" />
              Payments & invoices ({d.invoiceTotal ?? 0})
            </div>
            {Array.isArray(d.invoices) && d.invoices.length > 0 ? (
              <div className="overflow-hidden rounded-lg border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-muted-foreground text-xs">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Date</th>
                      <th className="px-3 py-2 text-left font-medium">Description</th>
                      <th className="px-3 py-2 text-left font-medium">Gateway</th>
                      <th className="px-3 py-2 text-right font-medium">Amount</th>
                      <th className="px-3 py-2 text-left font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.invoices.map((inv: any, i: number) => (
                      <tr key={i} className="border-t">
                        <td className="whitespace-nowrap px-3 py-2">
                          {new Date(inv.paidAt || inv.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-3 py-2">{inv.lineItems?.[0]?.description || '—'}</td>
                        <td className="text-muted-foreground px-3 py-2">{inv.gateway}</td>
                        <td className="px-3 py-2 text-right font-medium">
                          {formatMoney(inv.amount, inv.currency)}
                        </td>
                        <td className="px-3 py-2">{statusBadge(inv.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-muted-foreground text-sm">No invoices yet.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
